import { QrcodeOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Col, DatePicker, Flex, Form, Modal, Popover, Row } from 'antd'
import { ModalProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'

import {
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceGetSpecimenEndpoint,
  useInvestigationsServiceHarvestSpecimenEndpoint
} from 'src/api/queries'
import { ApiError, QrCodeType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  InvestigationSpecimenTypesSelect,
  message,
  QRCode,
  SaveConfirm
} from 'src/components'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from './styles'

type Props = {
  id: string
  onCancel: () => void
} & ModalProps

export const AssignInvestigationModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()
  const [code, setCode] = useState<string>()

  const { styles } = useStyles()
  const [form] = Form.useForm()

  const specimenTypeId = Form.useWatch('specimenTypeId', form)

  const { data: investigationData, isLoading: isInvestigationLoading } =
    useInvestigationsServiceGetSpecimenEndpoint(
      { id: String(id) },
      [String(id)],
      { enabled: !!id }
    )

  const { mutate, isPending } = useInvestigationsServiceHarvestSpecimenEndpoint(
    {
      onError: (error: ApiError) => formErrorHandler(error.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
        })
        message.success(t('investigations.success_assigned_investigation'))
        onCancel()
      }
    }
  )

  const categoryIds = investigationData?.demand?.categories
    ?.map((cat) => cat.id)
    .filter((id): id is string => !!id)

  const onSubmit = async () => {
    const values = await form.validateFields()

    const payload = {
      harvestAt: dayjs(values.harvestDate).utc().format(),
      sentToLaboratoryAt: dayjs(values.sentToLaboratoryAt).utc().format(),
      specimenTypeId: values?.specimenTypeId
    }

    mutate({ requestBody: payload, id })
  }

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrintCode = useReactToPrint({
    contentRef: printableRef,
    onAfterPrint: () => {
      setCode(undefined)
    }
  })

  useEffect(() => {
    form.setFieldsValue({
      harvestDate: dayjs(),
      sentToLaboratoryAt: dayjs()
    })
  }, [])

  return (
    <Modal
      open
      centered
      maskClosable={false}
      title={t('investigations.assign_investigation_demand')}
      footer={null}
      width={520}
      onCancel={onCancel}
      {...props}
    >
      <Flex vertical gap={24}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Row gutter={[16, 24]}>
            <Col span={24}>
              <Form.Item
                name="specimenTypeId"
                label={t('consultations.specimen_type')}
                rules={requiredRules}
              >
                <InvestigationSpecimenTypesSelect
                  categoryIds={categoryIds}
                  isLoading={isInvestigationLoading}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="harvestDate"
                label={t('investigations.harvest_date')}
                rules={requiredRules}
              >
                <DatePicker
                  format={dayMonthYearHourMinutesFormatWithDot}
                  className="full-width"
                  showTime
                  needConfirm={false}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="sentToLaboratoryAt"
                label={t('investigations.sent_to_laboratory_at')}
                rules={requiredRules}
              >
                <DatePicker
                  format={dayMonthYearHourMinutesFormatWithDot}
                  className="full-width"
                  showTime
                  needConfirm={false}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Popover
                placement="bottom"
                trigger={['click']}
                onOpenChange={(open) => {
                  setCode(open ? investigationData?.code : undefined)
                }}
                content={
                  <Flex vertical gap={8} align="center">
                    <QRCode type={QrCodeType.SPECIMEN} content={id} />

                    <Button onClick={() => onPrintCode()}>
                      {t('analysis.add_test.print_code')}
                    </Button>
                  </Flex>
                }
              >
                <Button icon={<QrcodeOutlined />}>
                  {t('qr_code.view_qr_code')}
                </Button>
              </Popover>
            </Col>
          </Row>
        </Form>

        <div ref={printableRef} className={styles.code}>
          {code}
        </div>

        <Flex justify="center" gap={8}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${t('common.confirm')}?`}
            onConfirm={onSubmit}
            okButtonProps={{ loading: isPending }}
          >
            <Button type="primary" disabled={!specimenTypeId}>
              {t('common.confirm')}
            </Button>
          </SaveConfirm>
        </Flex>
      </Flex>
    </Modal>
  )
}
